import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PERM_LABEL = {
  dashboard: "Dashboard", transactions: "Transactions", banks: "Banking",
  ar: "Receivables", ap: "Payables", coa: "Chart of Accounts",
  accounting: "Financial Reports", financials: "Fixed Assets & Tax",
  payroll: "Payroll", inventory: "Inventory", ai: "AI Insights",
  executive: "Executive KPIs", reports: "Statement PDFs", analytics: "Analytics",
  rules: "Rules", budgets: "Budgets", accounts: "Accounts",
  categories: "Categories", goals: "Goals", investments: "Investments",
  recurring: "Recurring", entities: "Entities", team: "Team",
  settings: "Settings",
};

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, invite: null, error: "No invitation token provided." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/team/invites/lookup/${token}`);
        setState({ loading: false, invite: data, error: null });
      } catch (err) {
        setState({
          loading: false, invite: null,
          error: err?.response?.data?.detail || "This invitation is invalid or has expired.",
        });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/team/invites/accept", { token, password, name: name || undefined });
      toast.success("Welcome to the team!");
      if (refreshMe) await refreshMe();
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm text-[var(--muted-text)]">Loading invitation…</div>
      </div>
    );
  }

  if (state.error || !state.invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="invite-error">
        <div className="card-flat p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-8 h-8 mx-auto text-[#D4A017] mb-3" />
          <h1 className="font-display text-2xl">Invitation unavailable</h1>
          <p className="text-sm text-[var(--muted-text)] mt-2">{state.error}</p>
          <Link to="/login" className="inline-block mt-6 text-sm text-[var(--brand)] hover:underline">
            Go to sign in →
          </Link>
        </div>
      </div>
    );
  }

  const inv = state.invite;
  const permList = (inv.permissions || []).filter((p) => p !== "dashboard" && p !== "settings");

  return (
    <div className="min-h-screen bg-white flex flex-col lg:grid lg:grid-cols-2" data-testid="accept-invite-page">
      {/* Form column */}
      <section className="flex flex-col justify-between px-6 sm:px-10 lg:px-16 py-8">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[var(--brand)] text-white flex items-center justify-center font-display font-bold">P</div>
          <div className="font-display text-lg font-semibold">Accountrix</div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 flex-1 flex flex-col justify-center py-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--muted-text)]">Team invitation</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">You're invited.</h1>
          <p className="mt-2 text-sm text-[var(--muted-text)]">
            <b>{inv.owner_name}</b> has invited you to join their Accountrix workspace as{" "}
            <span className="text-[var(--text)]">{inv.email}</span>.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="accept-invite-form">
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Your name (optional)</Label>
              <Input
                data-testid="accept-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Accountant"
                className="mt-1 h-11 rounded-md"
              />
            </div>
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Set a password</Label>
              <Input
                data-testid="accept-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1 h-11 rounded-md"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="accept-submit"
              className="w-full h-11 rounded-md btn-brand"
            >
              {submitting ? "Accepting…" : (<>Accept &amp; sign in <ArrowRight className="ml-2 w-4 h-4" /></>)}
            </Button>
          </form>

          <p className="text-xs text-[var(--muted-text)] mt-6">
            Already have an account with this email?{" "}
            <Link to="/login" className="text-[var(--brand)] hover:underline">Sign in instead</Link>{" "}
            — your existing password will be overwritten by the one you set above.
          </p>
        </div>

        <div className="text-xs text-[var(--muted-text)] pt-6 border-t border-[var(--divider)]">
          By continuing you agree to our Terms · Privacy
        </div>
      </section>

      {/* Access summary */}
      <section className="hidden lg:flex relative bg-[var(--brand)] text-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-[0.07]"
             style={{
               backgroundImage: "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
               backgroundSize: "40px 40px",
             }} />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 py-12 w-full max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-white/70">Your access</div>
          <h2 className="font-display text-3xl xl:text-4xl mt-3">
            {permList.length > 0 ? `${permList.length} modules` : "Dashboard access"}
          </h2>
          <p className="mt-3 text-sm text-white/85">
            The workspace owner has scoped your access to just what you need.
          </p>
          <ul className="mt-10 space-y-2.5" data-testid="access-summary">
            {permList.length === 0 ? (
              <li className="flex items-center gap-3 text-sm text-white/90">
                <CheckCircle2 className="w-5 h-5 shrink-0" /> Dashboard (read only)
              </li>
            ) : (
              permList.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-white/90">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <span>{PERM_LABEL[p] || p}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}
